import * as React from "react";
import clsx from "clsx";
import { Send, CircleStop, Maximize2, Minimize2 } from "lucide-react";
import { useToggle } from "react-use";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import { KeyBoardCodeEnter } from "@/types/keybord";
import * as analytics from "@/services/analytics";

interface Props {
  onSendMessage: (message: string) => void;
  isStreaming: boolean;
  isReady: boolean;
  placeholder?: string;
  compact?: boolean;
  bottomSlot?: React.ReactNode;
  onInputChange?: (value: string) => void;
  onCancel?: () => void;
}

const MIN_HEIGHT = 38;
const MAX_HEIGHT = 120; // ~5 lines

export const ChatInput = React.memo(
  ({
    onSendMessage,
    isStreaming,
    isReady,
    placeholder = "Submit a message...",
    compact = false,
    bottomSlot,
    onInputChange,
    onCancel,
  }: Props) => {
    const [inputMessage, setInputMessage] = React.useState("");
    const [isExpanded, toggleExpanded] = useToggle(false);
    const [textareaHeight, setTextareaHeight] = React.useState(MIN_HEIGHT);
    const [hasOverflow, setHasOverflow] = React.useState(false);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      if (!inputMessage) {
        setTextareaHeight(MIN_HEIGHT);
        setHasOverflow(false);
        return;
      }

      const currentHeight = textarea.style.height;
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = currentHeight;

      const newHeight = Math.min(Math.max(scrollHeight, MIN_HEIGHT), MAX_HEIGHT);
      setTextareaHeight(newHeight);
      setHasOverflow(scrollHeight > MAX_HEIGHT);
    }, [inputMessage]);

    const handleSendMessage = React.useCallback(() => {
      const prompt = inputMessage.trim();
      if (prompt) {
        onSendMessage(prompt);
        analytics.promptEnter(prompt);
        setInputMessage("");
        toggleExpanded(false);
      }
    }, [inputMessage, onSendMessage, toggleExpanded]);

    const handleKeyPress = (event: React.KeyboardEvent) => {
      if (event.code === KeyBoardCodeEnter && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    };

    const isDisabled = isStreaming || !isReady;

    return (
      <div className={compact ? "" : "border-t border-input px-6 py-4"}>
        <div className="rounded-lg border border-input bg-background px-4 py-2 shadow-sm">
          <div className="flex items-end gap-3">
            <textarea
              ref={textareaRef}
              rows={1}
              value={inputMessage}
              onChange={e => {
                setInputMessage(e.target.value);
                onInputChange?.(e.target.value);
              }}
              onKeyDown={handleKeyPress}
              placeholder={placeholder}
              className="flex-1 resize-none border-none bg-transparent py-2 text-sm text-foreground outline-none transition-all duration-300 ease-in-out placeholder:text-muted-foreground"
              style={{ height: isExpanded ? 600 : textareaHeight }}
              disabled={isDisabled}
            />
            {(hasOverflow || isExpanded) && (
              <Tooltip text={isExpanded ? "Collapse input" : "Expand input"} className="bg-gray-900 text-sm text-white">
                <Button
                  variant="ghost"
                  size="icon"
                  className={clsx(
                    "mb-1 h-8 w-8 flex-shrink-0 rounded-lg text-muted-foreground transition-all duration-300 hover:text-foreground",
                    isExpanded && "rotate-180",
                  )}
                  onClick={toggleExpanded}
                  disabled={isDisabled}
                >
                  {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </Button>
              </Tooltip>
            )}
            {isStreaming && onCancel ? (
              <Button
                variant="secondary"
                size="icon"
                className="mb-1 h-8 w-8 flex-shrink-0 rounded-lg"
                onClick={onCancel}
                title="Stop generating"
              >
                <CircleStop size={16} />
              </Button>
            ) : (
              <Button
                variant="coral"
                size="icon"
                className="mb-1 h-8 w-8 flex-shrink-0 rounded-lg"
                onClick={handleSendMessage}
                disabled={isDisabled || !inputMessage.trim()}
                title="Send message"
              >
                <Send size={16} />
              </Button>
            )}
          </div>
          {bottomSlot}
        </div>
      </div>
    );
  },
);
